import React, { useContext } from 'react'
import classNames from 'classnames'

import CurrentPathContext from 'marketing-site/src/components/context/CurrentPathContext'
import LocalesContext from 'marketing-site/src/components/context/LocalesContext'
import { mq } from 'marketing-site/src/library/utils'
import useLocaleCode from 'marketing-site/src/library/hooks/useLocaleCode'
import useTranslate from 'marketing-site/src/library/hooks/useTranslate'
import { localiseURL } from 'marketing-site/src/library/utils/localiseURL'
import { Text } from 'marketing-site/src/library/elements/Text'
import { LocalePicker } from 'marketing-site/src/mvp/library/elements/LocalePicker'
import { IProps } from '..'

export type IUtilityNavProps = IProps & {
  isInView: boolean
}

export function HeaderNavigationUtilityNav({
  contentType = 'flex',
  theme = 'light',
  signInLink,
  isInView,
}: IUtilityNavProps): React.ReactElement {
  const localeCode = useLocaleCode()
  const { translate } = useTranslate()
  const locales = useContext(LocalesContext)
  const currentPath = useContext(CurrentPathContext)

  const onLocaleChange = (newLocaleCode: string) => {
    window.location.href = localiseURL(currentPath.plain, newLocaleCode)
  }

  const classes = classNames('header-navigation-utility-nav', {
    [`header-navigation-utility-nav--theme-${theme}`]: theme,
  })

  return (
    <>
      <nav id="header-navigation-utility-nav" className={classes}>
        <div className="header-navigation-utility-nav__inner">
          <ol className="header-navigation-utility-nav__menu">
            <li role="menuitem">
              <LocalePicker
                contentType={contentType}
                locales={locales}
                selected={localeCode}
                onChange={onLocaleChange}
                id="locale-picker"
              />
            </li>
            <li role="menuitem" className="header-navigation-utility-nav__menu-item">
              <a href={localiseURL('/contact-sales', localeCode)}>
                <Text brand={contentType === 'flex' ? 'default' : 'mvp'} size="caption">
                  {translate('Contact Sales')}
                </Text>
              </a>
            </li>
            {signInLink && (
              <li role="menuitem" className="header-navigation-utility-nav__menu-item is-hidden">
                <a href={signInLink.url} data-testid="header-navigation-sign-in-link">
                  <Text brand={contentType === 'flex' ? 'default' : 'mvp'} size="caption">
                    {signInLink.title}
                  </Text>
                </a>
              </li>
            )}
          </ol>
        </div>
      </nav>

      <style jsx>
        {`
          .header-navigation-utility-nav {
            width: 100%;
            height: var(--utility-nav-height);
            padding-left: 1.25rem;
            padding-right: 1.25rem;
            position: relative;
            transition: background-color 0.32s;
            // Place it right above the Header Navigation.
            z-index: 110;

            &__inner {
              max-width: ${contentType === 'flex' ? '1290px' : '77.5rem'};
              height: 100%;
              margin: 0 auto;
            }

            &__menu {
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 25px;
              position: relative;
            }

            &__menu-item {
              a :global(span) {
                color: inherit;
              }

              &.is-hidden {
                display: none;
              }
            }

            &--theme-light {
              background-color: ${contentType === 'flex' ? 'var(--white)' : 'var(--paper)'};
              color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};

              .header-navigation-utility-nav__menu-item {
                a {
                  color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};
                }
              }
            }

            &--theme-dark,
            &--theme-resolution-bot {
              background-color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};
              color: ${contentType === 'flex' ? 'var(--white)' : 'var(--paper)'};

              .header-navigation-utility-nav__menu-item {
                a {
                  color: ${contentType === 'flex' ? 'var(--white)' : 'var(--paper)'};
                }
              }
            }
          }

          @media (${mq.tablet}) {
            .header-navigation-utility-nav {
              padding-left: 2rem;
              padding-right: 2rem;

              &__inner {
                height: 30px;
                display: flex;
                justify-content: flex-end;
              }

              &__menu {
                align-items: flex-end;
                padding-bottom: 0.235rem;
              }

              &__menu-item {
                &.is-hidden {
                  display: initial;
                }
              }
            }
          }

          @media (${mq.desktop}) {
            .header-navigation-utility-nav {
              padding-left: 2.5rem;
              padding-right: 2.5rem;
              margin-bottom: -1.5rem;

              &__inner {
                height: unset;
                padding-top: ${isInView ? '10px' : '0'};
                transition: padding-top 0.25s ease-out;
              }
            }
          }
        `}
      </style>
    </>
  )
}
